import { useState } from 'react';
import { EnvelopeSimple, MapPin, Phone, PaperPlaneTilt, Spinner, Warning } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import API_URL from '../utils/apiUrl';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Failed to send message. Please try again.');
      }
    } catch {
      setError('Could not connect. Please try again later.');
    }
    setSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <SEO
          title="Contact Us"
          description="Get in touch with Grade9Ready. Questions about GCSE revision, subscriptions, or our platform? We'd love to hear from you."
          path="/contact"
        />
        <motion.div 
          className="neo-card text-center max-w-md"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-16 h-16 bg-emerald-500 rounded-full border-2 border-foreground flex items-center justify-center mx-auto mb-4 shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
            <PaperPlaneTilt size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-2xl font-heading font-bold mb-2">Message Sent!</h1>
          <p className="text-muted-foreground mb-4">
            Thank you for getting in touch. We'll aim to respond within 24 hours.
          </p>
          <a href="/" className="neo-btn inline-block">Back to Home</a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <SEO
        title="Contact Us — Get in Touch"
        description="Contact Grade9Ready with questions about our GCSE revision platform, subscriptions, or how we can help your child achieve Grade 9."
        path="/contact"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
            Get in Touch
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Have a question, suggestion, or need help? We'd love to hear from you.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Contact Info */}
          <motion.div 
            className="md:col-span-1 space-y-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="neo-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary rounded-lg border-2 border-foreground flex items-center justify-center shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]">
                  <EnvelopeSimple size={24} weight="bold" className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold">Email</h3>
                  <a href="mailto:hello@grade9ready.com" className="text-muted-foreground hover:text-primary">
                    hello@grade9ready.com
                  </a>
                </div>
              </div>
            </div>

            <div className="neo-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent rounded-lg border-2 border-foreground flex items-center justify-center shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]">
                  <MapPin size={24} weight="bold" />
                </div>
                <div>
                  <h3 className="font-bold">Location</h3>
                  <span className="text-muted-foreground">Manchester, United Kingdom</span>
                </div>
              </div>
            </div>

            <div className="neo-card bg-secondary">
              <h3 className="font-bold mb-2">Support Hours</h3>
              <p className="text-sm text-muted-foreground">
                Monday - Friday: 9am - 6pm GMT<br />
                Weekend: Email support only
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div 
            className="md:col-span-2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="neo-card">
              <h2 className="font-heading font-bold text-xl mb-6">Send us a message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold mb-2">
                      Your Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      className="neo-input w-full"
                      placeholder="John Smith"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold mb-2">
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="neo-input w-full"
                      placeholder="you@example.com"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="neo-input w-full"
                    placeholder="How can we help you?"
                    required
                    data-testid="contact-message"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm" data-testid="contact-error">
                    <Warning size={16} /> {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="neo-btn w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <>
                      <Spinner size={20} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt size={20} weight="bold" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
